import type { FC, ReactNode } from 'react'
import { useRef } from 'react'

import { cn } from '@/lib/utils'

import type { ParsedTemplateVariable } from './template-variable.interface'
import { useTemplateVariableEditor } from './use-template-variable-editor.hook'

import './template-variable-editor.css'

interface TemplateVariableEditorProps {
  value: string
  onChange: (value: string) => void
  onBlur?: () => void
  onVariablesChange?: (variableList: ParsedTemplateVariable[]) => void
  placeholder?: string
  isDisabled?: boolean
  minHeight?: string
  className?: string
}

/**
 * Rich text editor for template content with inline variable highlighting
 * and autocomplete for variable validators. Uses CodeMirror 6 under the hood.
 *
 * Variables follow syntax: {variableName:type:constraint(value):modifier(value)}
 * They are visually highlighted as blue inline badges during editing.
 */
export const TemplateVariableEditor: FC<TemplateVariableEditorProps> = ({
  value,
  onChange,
  onBlur,
  onVariablesChange,
  placeholder = 'Enter template content. Type { to insert a variable...',
  isDisabled = false,
  minHeight = '200px',
  className,
}): ReactNode => {
  const containerRef = useRef<HTMLDivElement>(null)

  useTemplateVariableEditor({
    containerRef,
    value,
    onChange,
    onBlur,
    onVariablesChange,
    placeholder,
    isDisabled,
    minHeight,
  })

  return (
    <div
      ref={containerRef}
      className={cn(
        'template-variable-editor-wrapper',
        'w-full cursor-text rounded-md border border-input bg-transparent shadow-xs',
        'transition-[color,box-shadow] outline-none',
        'focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px]',
        'dark:bg-input/30',
        isDisabled && 'cursor-not-allowed opacity-50 pointer-events-none',
        className
      )}
    />
  )
}

TemplateVariableEditor.displayName = 'TemplateVariableEditor'
